import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Select, Row, Input } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  getCustomers,
  clearCustomers,
  clearProductIds,
  getProductIds,
  getFactoryIds,
  clearFactoryIds,
  getRackIds,
  clearRackIds,
  getAccessoriesForJobchart,
  clearAccessoriesForJobchart,
  getActiveJobchartIds,
  clearActiveJobchartIds,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntInputTextArea = GetField(Input.TextArea);

const AddAccessoryInInventory = props => {
  const { classes } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, getValues, watch, setValue } = useForm();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const customers = useSelector(state => state.customers);
  const productIds = useSelector(state => state.productIds);
  const activeJobchartIds = useSelector(state => state.activeJobchartIds);
  const factoryIds = useSelector(state => state.factoryIds);
  const rackIds = useSelector(state => state.rackIds);
  const accessoriesForJobchart = useSelector(
    state => state.accessoriesForJobchart
  );

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getFactoryIds());
    dispatch(getRackIds());
    dispatch(getAccessoriesForJobchart());
    return () => {
      dispatch(clearCustomers());
      dispatch(clearFactoryIds());
      dispatch(clearRackIds());
      dispatch(clearAccessoriesForJobchart());
    };
  }, []);

  useEffect(() => {
    const customerId = getValues('customerId');
    if (!customerId) {
      dispatch(clearProductIds());
      setValue('productId', null);
    } else {
      dispatch(getProductIds(customerId));
    }
  }, [watch('customerId')]);

  useEffect(() => {
    const productId = getValues('productId');
    if (!productId) {
      dispatch(clearActiveJobchartIds());
      setValue('jobchartId', null);
    } else {
      dispatch(getActiveJobchartIds({ productId }));
    }
  }, [watch('productId')]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post('/accessory-inventory', formValues);
      successNotification('Accessory added to inventory');
      history.push('/inventory/accessory');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Accessory Inventory"
        moduleLink="/inventory/accessory"
        subModuleName="Add"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="productId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Product"
                  allowClear
                  placeholder="Select product"
                  {...field}
                >
                  {productIds.map(product => (
                    <Option key={product.id} value={product.id}>
                      {product.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="jobchartId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="BPO #"
                  allowClear
                  placeholder="Select bpo"
                  {...field}
                >
                  {activeJobchartIds.map(jobchart => (
                    <Option key={jobchart.id} value={jobchart.id}>
                      {jobchart.bpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="accessoryId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Accessory"
                  placeholder="Select accessory"
                  {...field}
                >
                  {accessoriesForJobchart.map(accessory => (
                    <Option key={accessory.id} value={accessory.id}>
                      {accessory.accessory}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="factoryId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Factory"
                  placeholder="Select factory"
                  {...field}
                >
                  {factoryIds.map(factory => (
                    <Option key={factory.id} value={factory.id}>
                      {factory.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="racknoId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Rack #"
                  placeholder="Select rack"
                  {...field}
                >
                  {rackIds.map(rack => (
                    <Option key={rack.id} value={rack.id}>
                      {rack.rackno}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="quantity"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  type="number"
                  min={0}
                  step="any"
                  label="Quantity"
                  placeholder="Quantity"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

AddAccessoryInInventory.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(AddAccessoryInInventory);
